import { XanoClient } from '@xano/js-sdk';
import { XanoRealtimeChannel } from '@xano/js-sdk/lib/models/realtime-channel';
import { XanoRealtimeState } from '@xano/js-sdk/lib/models/realtime-state';
import { create } from 'zustand';

import { RealtimeMessage } from '@/types/realtime-xano';

const xano = new XanoClient({
  instanceBaseUrl: import.meta.env.VITE_XANO_API_URL,
  realtimeConnectionHash: import.meta.env.VITE_XANO_API_KEY,
});

export const NOTIFICATIONS_CHANNEL_PREFIX = 'notifications-channel';
export const NOTIFICATIONS_USER_CHANNEL_PREFIX = 'notifications_user_';
export const CHAT_USER_CHANNEL_PREFIX = 'chat_user_';
export const CHAT_CHANNEL_PREFIX = 'chat_channel_';
export const FEED_CHANNEL_PREFIX = 'feed-channel';

export function getNotificationsChannel(companyId: string | undefined) {
  return companyId ? `${NOTIFICATIONS_CHANNEL_PREFIX}/${companyId}` : null;
}

export function getNotificationsUserChannel(userId: string | undefined) {
  return userId ? `${NOTIFICATIONS_USER_CHANNEL_PREFIX}/${userId}` : null;
}

export function getChatUserChannel(userId: string | undefined) {
  return userId ? `${CHAT_USER_CHANNEL_PREFIX}/${userId}` : null;
}

export function getChatChannel(companyId: string | undefined) {
  return companyId ? `${CHAT_CHANNEL_PREFIX}/${companyId}` : null;
}

export function getFeedChannel(companyId: string | undefined) {
  return companyId ? `${FEED_CHANNEL_PREFIX}/${companyId}` : null;
}

export default xano;

type ChannelListener = (data: RealtimeMessage<any>) => void;

type XanoChannels = {
  // Active open channels
  channels: Record<string, XanoRealtimeChannel>;

  // Channels that are waiting for websocket connection to be open
  pendingChannels: Record<string, boolean>;

  addChannel: (channelName: string) => void;

  removeChannel: (channelName: string) => void;

  addListener: (channelName: string, listener: ChannelListener) => void;

  removeListener: (channelName: string, listener: ChannelListener) => void;
};

/**
 * Desired listeners per channel. Used to cancel racy attach work under
 * React StrictMode (mount → cleanup → remount) and agent-driven handler swaps.
 */
const desiredListeners = new Map<string, Set<ChannelListener>>();

/** Pending deferred attaches so cleanup can cancel them before they run. */
const pendingAttaches = new Map<
  ChannelListener,
  ReturnType<typeof setTimeout>
>();

const getDesiredListeners = (channelName: string) => {
  let listeners = desiredListeners.get(channelName);
  if (!listeners) {
    listeners = new Set();
    desiredListeners.set(channelName, listeners);
  }
  return listeners;
};

const detachListenerFromChannel = (
  channel: XanoRealtimeChannel | undefined,
  listener: ChannelListener
) => {
  if (!channel) return;

  const onFuncs = (channel as any).onFuncs as
    | Array<{ onFunc: ChannelListener }>
    | undefined;
  if (!onFuncs) return;

  const index = onFuncs.findIndex(({ onFunc }) => onFunc === listener);
  if (index !== -1) {
    onFuncs.splice(index, 1);
  }
};

const isListenerAttached = (
  channel: XanoRealtimeChannel | undefined,
  listener: ChannelListener
) => {
  if (!channel) return false;
  const onFuncs = (channel as any).onFuncs as
    | Array<{ onFunc: ChannelListener }>
    | undefined;
  return Boolean(onFuncs?.some(({ onFunc }) => onFunc === listener));
};

const scheduleAttachListener = (
  channelName: string,
  listener: ChannelListener,
  getChannel: () => XanoRealtimeChannel | undefined
) => {
  const existing = pendingAttaches.get(listener);
  if (existing != null) {
    clearTimeout(existing);
  }

  // Deferred attach avoids an extra Xano "join" when registering immediately
  // after channel creation. Must remain cancellable for StrictMode cleanup.
  const timeoutId = setTimeout(() => {
    pendingAttaches.delete(listener);

    if (!desiredListeners.get(channelName)?.has(listener)) {
      return;
    }

    const channel = getChannel();
    if (!channel || isListenerAttached(channel, listener)) {
      return;
    }

    channel.on(listener);
  }, 0);

  pendingAttaches.set(listener, timeoutId);
};

const cancelPendingAttach = (listener: ChannelListener) => {
  const timeoutId = pendingAttaches.get(listener);
  if (timeoutId != null) {
    clearTimeout(timeoutId);
    pendingAttaches.delete(listener);
  }
};

const whenSocketReady = (callback: () => void) => {
  const xanoRealtimeState = XanoRealtimeState.getInstance();
  const socket = xanoRealtimeState.getSocket();

  if (socket?.readyState === WebSocket.CONNECTING) {
    socket.addEventListener('open', callback, { once: true });
    return;
  }

  callback();
};

export const useXanoChannelsStore = create<XanoChannels>((set, get) => ({
  channels: {},

  pendingChannels: {},

  addChannel: channelName => {
    const doAddChannel = () => {
      set(state => {
        // Skip if channel is already open
        if (state.channels[channelName]) {
          return state;
        }

        // Skip if channel is not in pending list - this can happen if it was removed while in pending state
        if (!state.pendingChannels[channelName]) {
          return state;
        }

        const channel = xano.channel(channelName, {
          history: true,
          queueOfflineActions: true,
        });

        return {
          channels: {
            ...state.channels,
            [channelName]: channel,
          },
          pendingChannels: {
            ...state.pendingChannels,
            [channelName]: false,
          },
        };
      });

      // Attach any listeners that were registered before the channel opened.
      const listeners = desiredListeners.get(channelName);
      if (!listeners?.size) return;

      const channel = get().channels[channelName];
      if (!channel) return;

      listeners.forEach(listener => {
        scheduleAttachListener(channelName, listener, () =>
          get().channels[channelName]
        );
      });
    };

    const state = get();

    if (state.channels[channelName] || state.pendingChannels[channelName]) {
      return;
    }

    set(current => ({
      pendingChannels: { ...current.pendingChannels, [channelName]: true },
    }));

    whenSocketReady(doAddChannel);
  },

  removeChannel: channelName => {
    const doRemoveChannel = () => {
      set(state => {
        state.channels[channelName]?.destroy();

        const updatedChannels = { ...state.channels };
        delete updatedChannels[channelName];

        return {
          channels: updatedChannels,
        };
      });
    };

    set(state => ({
      pendingChannels: {
        ...state.pendingChannels,
        [channelName]: false,
      },
    }));

    whenSocketReady(doRemoveChannel);
  },

  addListener: (channelName, listener) => {
    getDesiredListeners(channelName).add(listener);

    const tryAttach = () => {
      if (!desiredListeners.get(channelName)?.has(listener)) {
        return;
      }

      scheduleAttachListener(channelName, listener, () =>
        get().channels[channelName]
      );
    };

    whenSocketReady(tryAttach);
  },

  removeListener: (channelName, listener) => {
    desiredListeners.get(channelName)?.delete(listener);
    cancelPendingAttach(listener);

    const detach = () => {
      detachListenerFromChannel(get().channels[channelName], listener);
    };

    // Detach synchronously when possible so StrictMode remount can't leave
    // both the old and new handlers active.
    detach();
    whenSocketReady(detach);
  },
}));
